import type { Action } from '@/core/input';
import { type ViewName, isViewName } from '@/core/viewport';

/**
 * Device-local preferences.
 *
 * Deliberately separate from the save game: control layout belongs to the
 * device and the player's hands, not to a playthrough. Copying a save to
 * another device should not drag its button layout along.
 *
 * Stored in localStorage rather than IndexedDB — it is a few hundred bytes read
 * once at boot, and a synchronous read keeps the controls correct on the very
 * first frame instead of popping into place.
 */

export type ButtonSlot = 'top' | 'left' | 'right' | 'bottom';

export const BUTTON_SLOTS: readonly ButtonSlot[] = ['top', 'right', 'bottom', 'left'];

/** Actions the player may place on a face button. */
export const ASSIGNABLE_ACTIONS: readonly Action[] = ['attack', 'dash', 'interact', 'special'];

export const SETTINGS_VERSION = 1;

export interface Settings {
  readonly version: number;
  readonly slots: Readonly<Record<ButtonSlot, Action>>;
  /** How much world is on screen. A device preference, like the buttons. */
  readonly view: ViewName;
}

/**
 * Frequency follows reach: the thumb pivots from the bottom-right corner, so
 * `bottom` is nearest and takes attack, while `top` is the longest stretch and
 * takes the deliberate, occasional Spirit action.
 */
export const DEFAULT_SETTINGS: Settings = {
  version: SETTINGS_VERSION,
  slots: {
    top: 'special',
    right: 'dash',
    bottom: 'attack',
    left: 'interact',
  },
  // Closer by default: on a phone the wide framing makes a 16px character too
  // small to read what she is holding, which is most of what combat is.
  view: 'close',
};

const STORAGE_KEY = 'heartland:settings';

export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Reassign a slot.
 *
 * If the action already sits elsewhere the two slots swap, rather than leaving
 * a duplicate. Duplicates would silently make one action unreachable — the
 * player would have to notice a missing verb and work out why.
 */
export function assignSlot(settings: Settings, slot: ButtonSlot, action: Action): Settings {
  const current = settings.slots[slot];
  if (current === action) return settings;

  const slots: Record<ButtonSlot, Action> = { ...settings.slots };
  const occupant = BUTTON_SLOTS.find((candidate) => slots[candidate] === action);

  slots[slot] = action;
  if (occupant) slots[occupant] = current;

  return { ...settings, slots };
}

/**
 * Coerce unknown stored data into usable settings.
 *
 * Each slot falls back independently, so one bad value does not discard the
 * player's other choices. Duplicates are resolved rather than rejected: a
 * layout with two attack buttons is recoverable, and throwing away the whole
 * configuration over it would be worse.
 */
export function normaliseSettings(value: unknown): Settings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  const rawSlots = (
    typeof raw.slots === 'object' && raw.slots !== null ? raw.slots : {}
  ) as Record<string, unknown>;

  const assigned = new Map<ButtonSlot, Action>();
  const taken = new Set<Action>();

  // 1. Honour every stored value that is valid and not already claimed.
  for (const slot of BUTTON_SLOTS) {
    const candidate = rawSlots[slot];
    if (isAssignable(candidate) && !taken.has(candidate)) {
      assigned.set(slot, candidate);
      taken.add(candidate);
    }
  }

  // 2. For the rest, prefer that slot's own default where it is still free, so
  //    a partial or damaged file lands as close to the standard layout as it can.
  for (const slot of BUTTON_SLOTS) {
    if (assigned.has(slot)) continue;
    const fallback = DEFAULT_SETTINGS.slots[slot];
    if (!taken.has(fallback)) {
      assigned.set(slot, fallback);
      taken.add(fallback);
    }
  }

  // 3. Whatever is still empty takes whatever is still unplaced. Slots and
  //    assignable actions are the same count, so this always completes.
  const unplaced = ASSIGNABLE_ACTIONS.filter((action) => !taken.has(action));
  for (const slot of BUTTON_SLOTS) {
    if (assigned.has(slot)) continue;
    const replacement = unplaced.shift();
    if (replacement) {
      assigned.set(slot, replacement);
      taken.add(replacement);
    }
  }

  return {
    version: SETTINGS_VERSION,
    slots: {
      top: assigned.get('top') ?? DEFAULT_SETTINGS.slots.top,
      right: assigned.get('right') ?? DEFAULT_SETTINGS.slots.right,
      bottom: assigned.get('bottom') ?? DEFAULT_SETTINGS.slots.bottom,
      left: assigned.get('left') ?? DEFAULT_SETTINGS.slots.left,
    },
    // An unrecognised view is a preference from a future build or a typo in
    // localStorage; either way the default is a better guess than nothing.
    view: isViewName(raw.view) ? raw.view : DEFAULT_SETTINGS.view,
  };
}

export function loadSettings(storage: SettingsStorage | null = defaultStorage()): Settings {
  if (!storage) return DEFAULT_SETTINGS;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return normaliseSettings(JSON.parse(raw));
  } catch (error) {
    console.warn('Could not read settings; using defaults.', error);
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(
  settings: Settings,
  storage: SettingsStorage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    // Private browsing and full quotas both throw here. A layout that does not
    // persist is a far smaller problem than a boot that fails.
    console.warn('Could not save settings.', error);
  }
}

export function clearSettings(storage: SettingsStorage | null = defaultStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Could not clear settings.', error);
  }
}

function isAssignable(value: unknown): value is Action {
  return typeof value === 'string' && (ASSIGNABLE_ACTIONS as readonly string[]).includes(value);
}

function defaultStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
